import React, { useState, useEffect } from "react";
import { Form, Button, Alert, Spinner, Modal, Row, Col } from "react-bootstrap";

import DocumentUploadForm from '../../components/document_upload_form';
import { uploadEachFile, describeUploadFailures } from '../../components/upload_utils';
import {
  postPersonalKycRequest,
  patchPersonalKycRequest,
  getNationalities,
  getDocumentTypesListRequest,
  uploadDocumentRequest,
} from "@/services";
import { useParams } from "react-router-dom";

const ShareholderKYCForm = ({
  shareholder = {},
  afterSubmit,
  onCancel,
  reloadPersonalKyc,
  documentsList,
  reloadDocumentsList
}) => {
  const { applicationId } = useParams();
  const isShareholderTypeBusiness = shareholder?.shareholder_type === "BUSINESS";
  const shareholderDocuments = documentsList.filter((doc) => doc.shareholder === shareholder?.id);
  const { kyc, identity, identity_type } = shareholder || {};
  const [formData, setFormData] = useState({
    loan_id: applicationId,
    name: kyc?.name || shareholder?.name || "",
    nationality: kyc?.nationality || "",
    passport_no: kyc?.passport_no || "",
    is_uae_resident: isShareholderTypeBusiness ? "No" : kyc?.is_uae_resident === false ? "No" : "Yes",
    identity: kyc?.identity || identity || "",
    identity_type: kyc?.identity_type || identity_type || "",
    shareholding_percentage: shareholder?.shareholding_percentage || "",
    address: kyc?.address || "",
    city: kyc?.city || undefined,
    postal_code: kyc?.postal_code || "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  // dropdowns
  const [nationalities, setNationalities] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const isKycVerified = shareholder?.kyc?.status === "verified";

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [nationalityRes, documentTypesRes] = await Promise.all([
          getNationalities(),
          getDocumentTypesListRequest({ category: "personal_kyc" }),
        ]);

        setNationalities(
          (nationalityRes?.results || []).map((t) => ({
            value: t.id,
            label: t.nationality,
          }))
        );
        setDocumentTypes(documentTypesRes?.results || []);
      } catch (err) {
        console.error("Failed to fetch dropdown options", err);
        setErrors({ general: "Failed to load KYC options. Please refresh." });
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, []);

  const visibleDocumentTypes = documentTypes.filter((doc) => {
    // hide Emirates ID for non-UAE residents
    if (doc.document_type_key === "emirates_id" && formData.is_uae_resident === "No") {
      return false;
    }
    // hide passport and utility_bill for business shareholders who are non-UAE residents
    if (["utility_bill", "passport"].includes(doc.document_type_key) && isShareholderTypeBusiness) {
      return false;
    }
    // hide company ID for individual shareholders
    if (doc.document_type_key === "company_id" && !isShareholderTypeBusiness) {
      return false;
    }
    return true;
  });

  // Business shareholders prove identity with a company ID; individuals always with a
  // passport, plus an Emirates ID when they are UAE residents.
  const isDocumentRequired = (doc) => {
    if (isShareholderTypeBusiness) {
      return doc.document_type_key === "company_id";
    }
    if (doc.document_type_key === "passport") return true;
    if (doc.document_type_key === "emirates_id") return formData.is_uae_resident === "Yes";
    return false;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onFileUpload = async (docType, event, afterEventCallback) => {
    const selectedFiles = Array.from(event.target.files);
    await uploadFile(docType, selectedFiles);
    if (afterEventCallback) afterEventCallback(docType);
  };

  const uploadFile = async (docType, selectedFiles) => {
    const doc = documentTypes.find((doc) => doc.document_type === docType);
    setUploading(true);
    // Clear only this document's error so other missing-document hints stay visible
    // eslint-disable-next-line no-unused-vars
    setErrors(({ general, [doc?.document_type_key]: _uploaded, ...rest }) => rest);

    try {
      const failures = await uploadEachFile(selectedFiles, (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('loan_id', applicationId);
        formDataUpload.append("document_type_id", doc.document_type);
        formDataUpload.append("shareholder_id", shareholder?.id);
        formDataUpload.append("file", file);
        return uploadDocumentRequest(formDataUpload);
      });
      if (failures.length) {
        setErrors((prev) => ({ ...prev, general: describeUploadFailures(failures, selectedFiles.length) }));
      }
      await reloadDocumentsList();
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        general: err?.message || "Failed to Upload Document. Try again.",
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isKycVerified) {
      if (afterSubmit) afterSubmit();
    } else {
      setSubmitting(true);
      setErrors({});

      // Basic validation
      const newErrors = {};
      if (!formData.name) newErrors.name = "Name is required";
      if (isShareholderTypeBusiness && !formData.identity) {
        newErrors.identity = "Company ID is required";
      }
      if (!isShareholderTypeBusiness && !formData.nationality) {
        newErrors.nationality = "Nationality is required";
      }
      // if (!formData.address) newErrors.address = "Address is required";
      // if (!formData.city) newErrors.city = "City is required";
      // if (!formData.postal_code) newErrors.postal_code = "Postal Code is required";

      // Flag every missing mandatory document at once, next to its upload field
      const missingDocs = visibleDocumentTypes.filter((doc) => {
        if (!isDocumentRequired(doc)) return false;
        const uploadedFiles = shareholderDocuments
          .filter((document) => document.document_type == doc.document_type)
          .flatMap((document) => document.document_files || []);
        return uploadedFiles.length === 0;
      });
      missingDocs.forEach((doc) => {
        newErrors[doc.document_type_key] = `Please upload ${doc.title}`;
      });

      if (Object.keys(newErrors).length > 0) {
        newErrors.general = missingDocs.length
          ? `Please complete the highlighted fields. Missing documents: ${missingDocs.map((doc) => doc.title).join(", ")}.`
          : "Please complete the highlighted fields.";
        setErrors(newErrors);
        setSubmitting(false);
        const firstKey = ["nationality", "identity"].find((key) => newErrors[key]);
        const firstTarget = firstKey
          ? document.getElementById(`shareholder-kyc-${firstKey}`)
          : document.getElementById(`doc-section-${missingDocs[0]?.document_type_key}`);
        firstTarget?.scrollIntoView({ behavior: "smooth", block: "center" });
        firstTarget?.focus?.();
        return;
      }
      try {
        let res;
        // An individual is identified by their passport, so identity mirrors the passport number.
        const payload = isShareholderTypeBusiness
          ? formData
          : {
              ...formData,
              identity: formData.passport_no || null,
              identity_type: "PASSPORT",
            };

        if (shareholder?.kyc?.uuid) {
          res = await patchPersonalKycRequest(shareholder?.kyc?.uuid, { ...payload, status: "completed" });
        } else {
          res = await postPersonalKycRequest({
            ...payload,
            shareholder: shareholder?.id,
            status: "completed",
          });
        }

        if (res?.success === false) {
          setErrors(res.error || { general: res.message || "Something went wrong" });
          return;
        }

        await reloadPersonalKyc();
        if (afterSubmit) afterSubmit();
      } catch (err) {
        setErrors({ general: err?.message || "Failed to submit KYC. Try again." });
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Form
      onSubmit={handleSubmit}
      className="p-3 border rounded shadow-sm mb-4 w-full"
      style={{ width: "100%" }}
    >
      <h5 className="mb-3">KYC for {shareholder.name}</h5>
      {["input_required", "rejected"].includes(kyc?.status) && kyc?.remarks ? (
        <p className="text-danger">{kyc.remarks}</p>
      ) : (
        ""
      )}
      <fieldset disabled={isKycVerified}>
        {/* ---- Personal KYC ---- */}

        <Row>
          <Col span={6}>
            <Form.Group className="mb-3" controlId="shareholder-kyc-name">
              <Form.Label>
                Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="name"
                value={formData.name}
                onChange={handleChange}
                isInvalid={!!errors.name}
                disabled
              />
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col span={6}>
            <Form.Group className="mb-3" controlId="shareholder-kyc-shareholding">
              <Form.Label>
                Shareholding (%) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                name="shareholding_percentage"
                value={formData.shareholding_percentage}
                disabled
              />
            </Form.Group>
          </Col>
        </Row>

        {!isShareholderTypeBusiness && (
          <Form.Group className="mb-3" controlId="shareholder-kyc-nationality">
            <Form.Label>
              Nationality <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
              isInvalid={!!errors.nationality}
              data-testid="fincobox-kyc-nationality"
            >
              <option value="">-- Select Nationality --</option>
              {nationalities.map((type) => (
                <option key={type.value} value={type.value} data-option-value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.nationality}</Form.Control.Feedback>
          </Form.Group>
        )}

        {!isShareholderTypeBusiness && (
          <Form.Group className="mb-3" controlId="shareholder-kyc-passport_no">
            <Form.Label>Passport No</Form.Label>
            <Form.Control
              name="passport_no"
              value={formData.passport_no}
              onChange={handleChange}
              isInvalid={!!errors.passport_no}
            />
            <Form.Control.Feedback type="invalid">{errors.passport_no}</Form.Control.Feedback>
          </Form.Group>
        )}

        {isShareholderTypeBusiness && (
          <Form.Group className="mb-3" controlId="shareholder-kyc-identity">
            <Form.Label>
              Company ID <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="identity"
              value={formData.identity}
              onChange={handleChange}
              isInvalid={!!errors.identity}
            />
            <Form.Control.Feedback type="invalid">{errors.identity}</Form.Control.Feedback>
          </Form.Group>
        )}

        {!isShareholderTypeBusiness && (
          <fieldset className="mb-3" data-testid="fincobox-kyc-uae-resident">
            <legend className="form-label fs-6 float-none mb-2">
              UAE Resident <span className="text-danger">*</span>
            </legend>
            {["Yes", "No"].map((option) => (
              <Form.Check
                key={option}
                inline
                type="radio"
                id={`shareholder-kyc-uae-resident-${option.toLowerCase()}`}
                name="is_uae_resident"
                value={option}
                label={option}
                checked={formData.is_uae_resident === option}
                onChange={handleChange}
                data-testid={`fincobox-kyc-uae-resident-${option.toLowerCase()}`}
              />
            ))}
          </fieldset>
        )}

        {/* Documents */}
        <DocumentUploadForm
          documentTypes={visibleDocumentTypes}
          isRequired={isDocumentRequired}
          errors={errors}
          documentsList={shareholderDocuments}
          reloadDocumentsList={reloadDocumentsList}
          showLoader={loadingOptions}
          onChange={onFileUpload}
          uploading={uploading}
          onUploadingChange={setUploading}
        />
      </fieldset>

      {errors.general && <Alert variant="danger">{errors.general}</Alert>}
      {/* ---- Actions ---- */}
      <div className="d-flex justify-content-end gap-2">
        <Button id="b_cancel_personal_kyc_form" variant="secondary" onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button
          id="b_submit_personal_kyc_form"
          type="submit"
          variant="primary"
          disabled={submitting || uploading}
          data-testid="fincobox-kyc-shareholder-save"
        >
          {submitting ? "Saving..." : uploading ? "Uploading..." : isKycVerified ? "Close" : "Save"}
        </Button>
      </div>
    </Form>
  );
};

export default ShareholderKYCForm;
